import './apple-pay-button';
import store from '../../../store/store';
import { CartPaymentMethod, CartPaymentMethodTemplate } from '../cart-payment-method';
import { PaymentDetails, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { isApplePaySupported } from './utils/apple-pay.util';
import { isLoadingBraintreeDependencies } from '../utils/braintree.util';
import { loading } from '../../../common/constants/api-constants';
import {
  updateBillingAddress,
  updateApplePayBillingAddress,
  updatePaymentDetails,
  updateShippingAddress
} from '../../checkout/shop-checkout';

/**
 * Apple Pay payment method that loads Apple Pay button in the shopping cart page.
 */
@customElement(`mhecomm-cart-apple-pay-payment-method`)
export class ApplePayCartPaymentMethod extends connect(store)(CartPaymentMethod) implements CartPaymentMethodTemplate {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mhecomm-apple-pay-checkout-info-collected', this.onCollectApplePayInfo);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-apple-pay-checkout-info-collected', this.onCollectApplePayInfo);
  }

  /**
   * Verifies if the dependencies for Apple Pay are being loaded.
   */
  override isLoadingDependencies(): boolean {
    return isLoadingBraintreeDependencies();
  }

  /**
   * Verifies if Apple Pay is enabled.
   * @param _state
   */
  override isPaymentMethodEnabled(_state: StoreState): boolean {
    return !!_state.shopCheckoutState.clientToken.data?.applePayEnabled && isApplePaySupported();
  }

  /**
   * Updates billing address fields in the store.
   */
  updateBillingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateBillingAddress(_address));
    store.dispatch(updateApplePayBillingAddress(_address));
  }

  /**
   * Updates shipping address fields in the store.
   */
  updateShippingAddress(_address: RmsBookstoreAddress): void {
    store.dispatch(updateShippingAddress(_address));
  }

  /**
   * Action to be executed when Apple Pay processing is complete.
   */
  terminatePaymentMethodProcessing(): void {
    return;
  }

  renderComp() {
    return html`
      <div class="${this.isLoading ? "d-none" : ""}">
        ${this.showPaymentButton ?
          html`
            <mhecomm-apple-pay-button
              amount="${this.totalAmount}"
              buttonColor="black"
              buttonType="check-out"
              buttonSize="large"
              clientToken="${this.clientToken}"
              currency="${this.currencyCode}"
              loaderMargins="mb-2"
              ?shippable=${this.shippable}>
            </mhecomm-apple-pay-button>
          ` : nothing}
      </div>
      <div>${this.isLoading ? html`${loading({m: 'mt-2'})}` : nothing}</div>`;
  }

  /**
   *
   * @param e collect Apple Pay billing address, shipping address and payment info from event details.
   * Validate and save payment info, billing and shipping address.
   */
  async onCollectApplePayInfo(e: Event): Promise<void> {
    const event = e as CustomEvent;
    const applePayPaymentDetails: PaymentDetails = event.detail.paymentDetails;

    store.dispatch(updatePaymentDetails(applePayPaymentDetails));

    await this.onCollectPaymentInfo(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-apple-pay-payment-method': ApplePayCartPaymentMethod;
  }
}
